import React, { useState, useEffect, useRef } from 'react';
import { Calculator, Play, RotateCcw, Download, Share2, Settings, PanelLeftClose, PanelLeftOpen } from 'lucide-react';

const GeogebraGraph = () => {
  const [equation, setEquation] = useState('');
  const [description, setDescription] = useState('');
  const [graphData, setGraphData] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [graphHistory, setGraphHistory] = useState([]);
  const [currentGraphIndex, setCurrentGraphIndex] = useState(-1);
  const [isLeftPanelCollapsed, setIsLeftPanelCollapsed] = useState(false);
  const geogebraRef = useRef(null);

  // Initialize Geogebra when component mounts
  useEffect(() => {
    const loadGeogebra = async () => {
      try {
        // Load Geogebra script if not already loaded
        if (!window.GGBApplet) {
          const script = document.createElement('script');
          script.src = 'https://www.geogebra.org/apps/deployggb.js';
          script.async = true;
          document.head.appendChild(script);
          
          script.onload = () => {
            initializeGeogebra();
          };
        } else {
          initializeGeogebra();
        }
      } catch (err) {
        setError('Failed to load Geogebra. Please check your internet connection.');
      }
    };

    loadGeogebra();
  }, []);

  const initializeGeogebra = () => {
    if (window.GGBApplet && geogebraRef.current) {
      const ggbApp = new window.GGBApplet({
        "appName": "graphing",
        "width": 800,
        "height": 600,
        "showToolBar": true,
        "showAlgebraInput": true,
        "showMenuBar": false,
        "enableLabelDrags": false,
        "enableShiftDragZoom": true,
        "enableRightClick": false,
        "showResetIcon": false,
        "useBrowserForJS": false
      }, true);

      ggbApp.inject('geogebra-container');
    }
  };

  const parseEquation = (input) => {
    // Convert common mathematical notation to Geogebra format
    let parsed = input
      .replace(/×/g, '*')
      .replace(/÷/g, '/')
      .replace(/²/g, '^2')
      .replace(/³/g, '^3')
      .replace(/π/g, 'pi')
      .replace(/√/g, 'sqrt')
      .replace(/sin\(/g, 'sin(')
      .replace(/cos\(/g, 'cos(')
      .replace(/tan\(/g, 'tan(')
      .replace(/log\(/g, 'log(')
      .replace(/ln\(/g, 'ln(')
      .replace(/e\^/g, 'exp(');

    return parsed;
  };

  const generateGraphFromEquation = async () => {
    if (!equation.trim()) {
      setError('Please enter an equation');
      return;
    }

    setIsLoading(true);
    setError('');

    try {
      const parsedEquation = parseEquation(equation);
      
      // Create graph data
      const newGraphData = {
        id: Date.now(),
        equation: equation,
        parsedEquation: parsedEquation,
        description: description,
        timestamp: new Date().toISOString()
      };

      setGraphData(newGraphData);
      setGraphHistory(prev => [...prev, newGraphData]);
      setCurrentGraphIndex(graphHistory.length);

      // Simulate API call to Geogebra
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      // In a real implementation, you would send the equation to Geogebra API
      // and get back the graph data
      console.log('Graph generated for equation:', parsedEquation);
      
    } catch (err) {
      setError('Failed to generate graph. Please check your equation format.');
    } finally {
      setIsLoading(false);
    }
  };

  const generateGraphFromDescription = async () => {
    if (!description.trim()) {
      setError('Please enter a description');
      return;
    }

    setIsLoading(true);
    setError('');

    try {
      // In a real implementation, you would use AI to convert description to equation
      // For now, we'll simulate this process
      const simulatedEquation = `y = ${description.includes('linear') ? 'x' : 
                                   description.includes('quadratic') ? 'x^2' : 
                                   description.includes('cubic') ? 'x^3' : 'x'}`;
      
      const newGraphData = {
        id: Date.now(),
        equation: simulatedEquation,
        parsedEquation: simulatedEquation,
        description: description,
        timestamp: new Date().toISOString()
      };

      setGraphData(newGraphData);
      setGraphHistory(prev => [...prev, newGraphData]);
      setCurrentGraphIndex(graphHistory.length);

      await new Promise(resolve => setTimeout(resolve, 1500));
      console.log('Graph generated from description:', description);
      
    } catch (err) {
      setError('Failed to generate graph from description.');
    } finally {
      setIsLoading(false);
    }
  };

  const clearGraph = () => {
    setGraphData(null);
    setEquation('');
    setDescription('');
    setError('');
  };

  const undoGraph = () => {
    if (currentGraphIndex > 0) {
      const newIndex = currentGraphIndex - 1;
      setCurrentGraphIndex(newIndex);
      setGraphData(graphHistory[newIndex]);
    }
  };

  const redoGraph = () => {
    if (currentGraphIndex < graphHistory.length - 1) {
      const newIndex = currentGraphIndex + 1;
      setCurrentGraphIndex(newIndex);
      setGraphData(graphHistory[newIndex]);
    }
  };

  const downloadGraph = () => {
    if (graphData) {
      const dataStr = JSON.stringify(graphData, null, 2);
      const dataBlob = new Blob([dataStr], { type: 'application/json' });
      const url = URL.createObjectURL(dataBlob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `graph-${graphData.id}.json`;
      link.click();
      URL.revokeObjectURL(url);
    }
  };

  const shareGraph = () => {
    if (graphData) {
      const shareData = {
        title: 'RightMaths Graph',
        text: `Check out this mathematical graph: ${graphData.equation}`,
        url: window.location.href
      };
      
      if (navigator.share) {
        navigator.share(shareData);
      } else {
        // Fallback: copy to clipboard
        navigator.clipboard.writeText(graphData.equation);
        alert('Graph equation copied to clipboard!');
      }
    }
  };

  return (
    <div className="max-w-7xl mx-auto p-6">
      <div className="text-center mb-8">
        <h1 className="text-4xl font-bold text-gray-800 mb-4">Geogebra 數學圖形繪製</h1>
        <p className="text-xl text-gray-600">輸入方程式或描述來生成數學圖形</p>
      </div>

      <div className="flex gap-4">
        {/* Left Panel */}
        <div className={`transition-all duration-300 ease-in-out ${
          isLeftPanelCollapsed ? 'w-16' : 'w-1/2'
        }`}>
          <div className="bg-white rounded-lg shadow-md p-4">
            {/* Panel Header with Toggle Button */}
            <div className="flex items-center justify-between mb-4">
              <h2 className={`font-bold ${isLeftPanelCollapsed ? 'hidden' : 'block'}`}>
                輸入面板
              </h2>
              <button
                onClick={() => setIsLeftPanelCollapsed(!isLeftPanelCollapsed)}
                className="p-2 rounded-lg hover:bg-gray-100 transition-colors"
                title={isLeftPanelCollapsed ? "展開面板" : "收起面板"}
              >
                {isLeftPanelCollapsed ? (
                  <PanelLeftOpen className="w-5 h-5 text-gray-600" />
                ) : (
                  <PanelLeftClose className="w-5 h-5 text-gray-600" />
                )}
              </button>
            </div>

            {/* Collapsible Content */}
            <div className={`transition-all duration-300 ease-in-out ${
              isLeftPanelCollapsed ? 'opacity-0 pointer-events-none' : 'opacity-100'
            }`}>
              {/* Input Panel */}
              <div className="space-y-6">
                {/* Equation Input */}
                <div className="bg-white rounded-lg shadow-md p-6">
                  <h2 className="text-2xl font-bold mb-4 flex items-center">
                    <Calculator className="w-6 h-6 mr-2 text-blue-500" />
                    方程式輸入
                  </h2>
                  
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        數學方程式
                      </label>
                      <textarea
                        value={equation}
                        onChange={(e) => setEquation(e.target.value)}
                        placeholder="例如: y = x^2 + 2x + 1, f(x) = sin(x), y = 2x + 3"
                        className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                        rows="4"
                      />
                    </div>
                    
                    <div className="flex space-x-2">
                      <button
                        onClick={generateGraphFromEquation}
                        disabled={isLoading || !equation.trim()}
                        className="flex-1 flex items-center justify-center space-x-2 px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors"
                      >
                        <Play className="w-4 h-4" />
                        <span>{isLoading ? '生成中...' : '生成圖形'}</span>
                      </button>
                      
                      <button
                        onClick={clearGraph}
                        className="px-4 py-2 bg-gray-500 text-white rounded-lg hover:bg-gray-600 transition-colors"
                      >
                        <RotateCcw className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                </div>

                {/* Description Input */}
                <div className="bg-white rounded-lg shadow-md p-6">
                  <h2 className="text-2xl font-bold mb-4">描述輸入</h2>
                  
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        圖形描述
                      </label>
                      <textarea
                        value={description}
                        onChange={(e) => setDescription(e.target.value)}
                        placeholder="例如: 一條通過原點的直線, 開口向上的拋物線, 正弦函數圖形"
                        className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                        rows="4"
                      />
                    </div>
                    
                    <button
                      onClick={generateGraphFromDescription}
                      disabled={isLoading || !description.trim()}
                      className="w-full flex items-center justify-center space-x-2 px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors"
                    >
                      <Play className="w-4 h-4" />
                      <span>{isLoading ? '分析中...' : '從描述生成'}</span>
                    </button>
                  </div>
                </div>

                {/* Quick Examples */}
                <div className="bg-white rounded-lg shadow-md p-6">
                  <h3 className="text-lg font-semibold mb-4">快速範例</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                    {[
                      { label: '直線', equation: 'y = 2x + 1' },
                      { label: '拋物線', equation: 'y = x^2 - 4' },
                      { label: '正弦函數', equation: 'y = sin(x)' },
                      { label: '指數函數', equation: 'y = 2^x' },
                      { label: '圓形', equation: 'x^2 + y^2 = 25' },
                      { label: '雙曲線', equation: 'x^2 - y^2 = 1' }
                    ].map((example, index) => (
                      <button
                        key={index}
                        onClick={() => setEquation(example.equation)}
                        className="p-2 text-sm bg-gray-100 hover:bg-gray-200 rounded transition-colors"
                      >
                        {example.label}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Error Display */}
                {error && (
                  <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                    <p className="text-red-700">{error}</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Graph Display */}
        <div className={`transition-all duration-300 ease-in-out ${
          isLeftPanelCollapsed ? 'w-full' : 'w-1/2'
        }`}>
          <div className="space-y-6">
            {/* Graph Container */}
            <div className="bg-white rounded-lg shadow-md p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-2xl font-bold">圖形顯示</h2>
                <div className="flex space-x-2">
                  <button
                    onClick={undoGraph}
                    disabled={currentGraphIndex <= 0}
                    className="p-2 text-gray-600 hover:text-gray-800 disabled:text-gray-300"
                    title="復原"
                  >
                    <RotateCcw className="w-4 h-4" />
                  </button>
                  <button
                    onClick={downloadGraph}
                    disabled={!graphData}
                    className="p-2 text-gray-600 hover:text-gray-800 disabled:text-gray-300"
                    title="下載"
                  >
                    <Download className="w-4 h-4" />
                  </button>
                  <button
                    onClick={shareGraph}
                    disabled={!graphData}
                    className="p-2 text-gray-600 hover:text-gray-800 disabled:text-gray-300"
                    title="分享"
                  >
                    <Share2 className="w-4 h-4" />
                  </button>
                </div>
              </div>

              {/* Geogebra Container */}
              <div 
                id="geogebra-container"
                ref={geogebraRef}
                className="w-full h-96 border border-gray-300 rounded-lg bg-gray-50 flex items-center justify-center"
              >
                {!graphData ? (
                  <div className="text-center text-gray-500">
                    <Calculator className="w-16 h-16 mx-auto mb-4" />
                    <p>輸入方程式或描述來生成圖形</p>
                  </div>
                ) : isLoading ? (
                  <div className="text-center text-gray-500">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto mb-4"></div>
                    <p>正在生成圖形...</p>
                  </div>
                ) : (
                  <div className="w-full h-full">
                    {/* In a real implementation, this would be the Geogebra applet */}
                    <div className="w-full h-full bg-gradient-to-br from-blue-50 to-purple-50 rounded-lg flex items-center justify-center">
                      <div className="text-center">
                        <div className="text-2xl font-bold text-gray-800 mb-2">
                          {graphData.equation}
                        </div>
                        <div className="text-sm text-gray-600">
                          生成時間: {new Date(graphData.timestamp).toLocaleString()}
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Graph History */}
            {graphHistory.length > 0 && (
              <div className="bg-white rounded-lg shadow-md p-6">
                <h3 className="text-lg font-semibold mb-4">圖形歷史</h3>
                <div className="space-y-2 max-h-48 overflow-y-auto">
                  {graphHistory.map((graph, index) => (
                    <div
                      key={graph.id}
                      onClick={() => {
                        setGraphData(graph);
                        setCurrentGraphIndex(index);
                      }}
                      className={`p-3 rounded-lg cursor-pointer transition-colors ${
                        currentGraphIndex === index ? 'bg-blue-100 border-l-4 border-blue-500' : 'bg-gray-50 hover:bg-gray-100'
                      }`}
                    >
                      <div className="text-sm font-medium">{graph.equation}</div>
                      <div className="text-xs text-gray-500">
                        {new Date(graph.timestamp).toLocaleString()}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default GeogebraGraph; 